import { useEffect, useState, useMemo } from 'react';
import { calendarApi } from '../utils/api';
import {
  CalendarDays, Plus, Trash2, Pencil, Loader, Check, X, Globe2, MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const HOLIDAY_TYPES = [
  { value: 'nacional', label: 'Nacional', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  { value: 'autonomico', label: 'Autonómico', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  { value: 'local', label: 'Local', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  { value: 'empresa', label: 'Empresa', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
];

const DELEGACIONES = [
  'todas', 'Madrid', 'Barcelona', 'Valencia', 'Sevilla', 'Bilbao',
  'Zaragoza', 'Málaga', 'Murcia', 'Palma', 'Las Palmas'
];

const defaultForm = {
  date: '',
  name: '',
  year: new Date().getFullYear(),
  type: 'nacional',
  location: 'todas'
};

export default function HolidayManagement() {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [dialog, setDialog] = useState(null); // null | 'create' | 'edit'
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterLocation, setFilterLocation] = useState('todas');

  useEffect(() => { loadHolidays(); }, [year]);

  const loadHolidays = async () => {
    setLoading(true);
    try {
      const res = await calendarApi.allHolidays(year);
      setHolidays(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setForm({ ...defaultForm, year });
    setEditId(null);
    setError('');
    setDialog('create');
  };

  const openEdit = (h) => {
    setForm({ date: h.date, name: h.name, year: h.year, type: h.type || 'nacional', location: h.location || 'todas' });
    setEditId(h.id);
    setError('');
    setDialog('edit');
  };

  const handleSave = async () => {
    if (!form.date || !form.name) {
      setError('Fecha y nombre son obligatorios');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (dialog === 'create') {
        await calendarApi.addHoliday({ ...form, year: parseInt(form.year) });
      } else {
        await calendarApi.updateHoliday(editId, { name: form.name, type: form.type, location: form.location });
      }
      await loadHolidays();
      setDialog(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este festivo?')) return;
    try {
      await calendarApi.deleteHoliday(id);
      await loadHolidays();
    } catch (e) {
      console.error(e);
    }
  };

  const getTypeInfo = (type) => HOLIDAY_TYPES.find(t => t.value === type) || HOLIDAY_TYPES[0];

  const filtered = useMemo(() =>
    holidays.filter(h => {
      if (filterType !== 'all' && h.type !== filterType) return false;
      if (filterLocation !== 'todas' && h.location !== filterLocation && h.location !== 'todas') return false;
      return true;
    }),
    [holidays, filterType, filterLocation]
  );

  const grouped = useMemo(() =>
    filtered.reduce((acc, h) => {
      const month = h.date.substring(0, 7);
      if (!acc[month]) acc[month] = [];
      acc[month].push(h);
      return acc;
    }, {}),
    [filtered]
  );

  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-brand" />
            Gestión de Festivos
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Configura el calendario de festivos por delegación
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
            <SelectTrigger className="w-28" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={openCreate} data-testid="btn-add-holiday" className="bg-brand hover:bg-brand-hover text-white gap-2">
            <Plus className="h-4 w-4" />
            Añadir festivo
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground font-medium">Tipo:</span>
          {['all', ...HOLIDAY_TYPES.map(t => t.value)].map(t => (
            <button
              key={t}
              onClick={() => setFilterType(t)}
              data-testid={`filter-type-${t}`}
              className={`text-xs px-2.5 py-1 rounded-full font-medium transition-all ${
                filterType === t
                  ? 'bg-brand text-white'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {t === 'all' ? 'Todos' : HOLIDAY_TYPES.find(h => h.value === t)?.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-4">
          <span className="text-xs text-muted-foreground font-medium">Delegación:</span>
          <Select value={filterLocation} onValueChange={setFilterLocation}>
            <SelectTrigger className="h-7 text-xs w-32" data-testid="filter-location">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DELEGACIONES.map(d => (
                <SelectItem key={d} value={d}>{d === 'todas' ? 'Todas' : d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {HOLIDAY_TYPES.map(({ value, label, color }) => {
          const count = holidays.filter(h => h.type === value).length;
          return (
            <div key={value} className="bg-card border border-border rounded-xl p-3 text-center shadow-sm">
              <div className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium mb-1 ${color}`}>{label}</div>
              <div className="text-xl font-bold font-heading">{count}</div>
              <div className="text-xs text-muted-foreground">festivos</div>
            </div>
          );
        })}
      </div>

      {/* Holiday list grouped by month */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader className="h-6 w-6 animate-spin text-brand" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-xl p-12 text-center">
          <CalendarDays className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="font-semibold font-heading text-sm mb-1">Sin festivos</p>
          <p className="text-xs text-muted-foreground">Añade festivos para este año y delegación</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.keys(grouped).sort().map(monthKey => {
            const [yr, mo] = monthKey.split('-');
            const monthName = monthNames[parseInt(mo) - 1];
            return (
              <div key={monthKey} className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
                <div className="px-4 py-2.5 bg-muted/50 border-b border-border">
                  <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    {monthName} {yr}
                  </span>
                </div>
                <div className="divide-y divide-border">
                  {grouped[monthKey].map(h => {
                    const typeInfo = getTypeInfo(h.type);
                    const isDefault = h.id?.startsWith('default-');
                    return (
                      <div
                        key={h.id}
                        className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
                        data-testid={`holiday-row-${h.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="text-center w-10">
                            <div className="text-lg font-bold font-heading leading-none">
                              {h.date.split('-')[2]}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'][new Date(h.date + 'T12:00:00').getDay()]}
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium">{h.name}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${typeInfo.color}`}>
                                {typeInfo.label}
                              </span>
                              {h.location && h.location !== 'todas' && (
                                <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
                                  <MapPin className="h-3 w-3" />
                                  {h.location}
                                </span>
                              )}
                              {(h.location === 'todas' || !h.location) && (
                                <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
                                  <Globe2 className="h-3 w-3" />
                                  Todas las delegaciones
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {!isDefault && (
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEdit(h)}
                              data-testid={`edit-holiday-${h.id}`}
                              className="h-7 w-7 p-0"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(h.id)}
                              data-testid={`delete-holiday-${h.id}`}
                              className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                        {isDefault && (
                          <span className="text-xs text-muted-foreground/60 italic">predefinido</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{dialog === 'create' ? 'Añadir festivo' : 'Editar festivo'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {dialog === 'create' && (
              <div className="space-y-1">
                <Label>Fecha *</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={e => setForm(f => ({ ...f, date: e.target.value, year: parseInt(e.target.value.split('-')[0] || year) }))}
                  data-testid="holiday-date-input"
                />
              </div>
            )}
            <div className="space-y-1">
              <Label>Nombre *</Label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Ej: Día de la Comunidad de Madrid"
                data-testid="holiday-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Tipo</Label>
                <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="holiday-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOLIDAY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Delegación</Label>
                <Select value={form.location} onValueChange={v => setForm(f => ({ ...f, location: v }))}>
                  <SelectTrigger data-testid="holiday-location-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DELEGACIONES.map(d => (
                      <SelectItem key={d} value={d}>{d === 'todas' ? 'Todas' : d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {error && <div className="text-sm text-destructive p-2 bg-destructive/10 rounded">{error}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-holiday-btn" className="bg-brand hover:bg-brand-hover text-white">
              {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
